/* Family tree layout helpers and shared constants */

export function buildGenerations(nodes) {
  if (!nodes.length) return { generations: {}, nodeMap: {} };

  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.node_id] = n; });

  const visited = new Set();
  const genMap = {};   // node_id -> generation number

  // Find root
  const root = nodes.find(n => n.is_root) || nodes[0];
  if (!root) return { generations: {}, nodeMap: {} };

  // BFS from root: root=0, parents=-1, children=+1
  const queue = [{ id: root.node_id, gen: 0 }];
  visited.add(root.node_id);
  genMap[root.node_id] = 0;

  // Also place root's partner at gen 0
  if (root.partner_id && nodeMap[root.partner_id]) {
    visited.add(root.partner_id);
    genMap[root.partner_id] = 0;
    queue.push({ id: root.partner_id, gen: 0 });
  }

  while (queue.length > 0) {
    const { id, gen } = queue.shift();
    const node = nodeMap[id];
    if (!node) continue;

    // Children: nodes whose parent_ids include this id
    nodes.forEach(child => {
      if (child.parent_ids?.includes(id) && !visited.has(child.node_id)) {
        visited.add(child.node_id);
        genMap[child.node_id] = gen + 1;
        queue.push({ id: child.node_id, gen: gen + 1 });
        // also place child's partner
        if (child.partner_id && nodeMap[child.partner_id] && !visited.has(child.partner_id)) {
          visited.add(child.partner_id);
          genMap[child.partner_id] = gen + 1;
          queue.push({ id: child.partner_id, gen: gen + 1 });
        }
      }
    });

    // Parents: nodes referenced in parent_ids
    (node.parent_ids || []).forEach(pid => {
      if (nodeMap[pid] && !visited.has(pid)) {
        visited.add(pid);
        genMap[pid] = gen - 1;
        queue.push({ id: pid, gen: gen - 1 });
        // also place parent's partner
        const parent = nodeMap[pid];
        if (parent.partner_id && nodeMap[parent.partner_id] && !visited.has(parent.partner_id)) {
          visited.add(parent.partner_id);
          genMap[parent.partner_id] = gen - 1;
          queue.push({ id: parent.partner_id, gen: gen - 1 });
        }
      }
    });

    // Partner
    if (node.partner_id && nodeMap[node.partner_id] && !visited.has(node.partner_id)) {
      visited.add(node.partner_id);
      genMap[node.partner_id] = gen;
      queue.push({ id: node.partner_id, gen });
    }
  }

  // Any unvisited nodes go to gen 0
  nodes.forEach(n => {
    if (!visited.has(n.node_id)) {
      genMap[n.node_id] = 0;
    }
  });

  // Group by generation
  const generations = {};
  Object.entries(genMap).forEach(([nid, g]) => {
    if (!generations[g]) generations[g] = [];
    generations[g].push(nodeMap[nid]);
  });

  // Sort within each generation: couples together
  Object.keys(generations).forEach(g => {
    const gen = generations[g];
    const placed = new Set();
    const sorted = [];
    gen.forEach(n => {
      if (placed.has(n.node_id)) return;
      sorted.push(n);
      placed.add(n.node_id);
      if (n.partner_id && !placed.has(n.partner_id)) {
        const partner = gen.find(p => p.node_id === n.partner_id);
        if (partner) {
          sorted.push(partner);
          placed.add(partner.node_id);
        }
      }
    });
    generations[g] = sorted;
  });

  return { generations, nodeMap, genMap };
}

export const GENDER_COLORS = {
  male: { bg: 'bg-sky-50', border: 'border-sky-300', ring: 'ring-sky-200', avatar: 'bg-sky-500', text: 'text-sky-700' },
  female: { bg: 'bg-rose-50', border: 'border-rose-300', ring: 'ring-rose-200', avatar: 'bg-rose-400', text: 'text-rose-700' },
  other: { bg: 'bg-amber-50', border: 'border-amber-300', ring: 'ring-amber-200', avatar: 'bg-amber-500', text: 'text-amber-700' },
};

export const GEN_LABELS = {
  '-3': 'Great-Grandparents',
  '-2': 'Grandparents',
  '-1': 'Parents',
  '0': 'You & Partner',
  '1': 'Children',
  '2': 'Grandchildren',
  '3': 'Great-Grandchildren',
};
